import { FormikErrors } from 'formik';
import { checkPhoneNumber, isDialCodeOnly } from 'utils/validation';
import isEmail from 'validator/lib/isEmail';
import { UserTypeEnum } from 'types/enums/User';
import { SignUpValues } from './types';

const maxCharacterLengthNameFields = 50;
const maxCharacterLengthEmailField = 255;
const nameRegex = /^[\p{L}\s\d'_.-]+$/u;

interface PasswordValidatorState {
  lowercase: boolean;
  uppercase: boolean;
  number: boolean;
  symbol: boolean;
  length: boolean;
  match: boolean;
}

const validatePasswordRules = (
  password?: string
): Partial<PasswordValidatorState> => {
  if (!password) return {};

  return {
    lowercase: /^(?=.*[a-z])/.test(password),
    uppercase: /^(?=.*[A-Z])/.test(password),
    number: /^(?=.*\d+)/.test(password),
    symbol: /^(?=.*[!@#$%ˆ&*^]+)/.test(password),
    length: /^(.{8,}$)/.test(password),
  };
};

export const updatePasswordValidator = (
  password?: string,
  passwordConfirmation?: string
): PasswordValidatorState => {
  const rules = validatePasswordRules(password);

  return {
    lowercase: rules.lowercase || false,
    uppercase: rules.uppercase || false,
    number: rules.number || false,
    symbol: rules.symbol || false,
    length: rules.length || false,
    match:
      password === passwordConfirmation && !!password && password.length >= 8,
  };
};

const validateName = (
  name: string | undefined,
  fieldName: string,
  t: any
): string | undefined => {
  if (!name || !name.trim()) {
    return t('common.forms.requiredField');
  }

  if (name.length > maxCharacterLengthNameFields) {
    return `${t(`common.forms.${fieldName}`)} ${t('signUp.validation.nameLength')} ${maxCharacterLengthNameFields} ${t('common.characters')}`;
  }

  if (!nameRegex.test(name)) {
    return `${t('signUp.validation.unallowedCharacters')} ${t(`common.forms.${fieldName}`)} ${t('common.forms.field')}`;
  }

  return undefined;
};

const validateEmail = (
  email: string | undefined,
  t: any
): string | undefined => {
  if (!email || !email.trim()) {
    return t('common.forms.requiredField');
  }

  if (!isEmail(email)) {
    return t('signUp.validation.emailInvalid');
  }

  if (email.length > maxCharacterLengthEmailField) {
    return `${t('common.forms.email')} ${t('signUp.validation.nameLength')} ${maxCharacterLengthEmailField} ${t('common.characters')}`;
  }

  return undefined;
};

const validatePassword = (
  password: string | undefined,
  passwordConfirmation: string | undefined,
  validator: PasswordValidatorState,
  t: any
): { password?: string; passwordConfirmation?: string } => {
  const errors: { password?: string; passwordConfirmation?: string } = {};

  if (!password || !password.trim()) {
    errors.password = t('common.forms.requiredField');
    return errors;
  }

  if (password.trim().length < 8) {
    errors.password = t('signUp.validation.length');
  } else if (!validator.lowercase) {
    errors.password = t('signUp.validation.lowercase');
  } else if (!validator.uppercase) {
    errors.password = t('signUp.validation.uppercase');
  } else if (!validator.number && !validator.symbol) {
    errors.password = t('signUp.validation.numberOrSymbol');
  }

  if (!passwordConfirmation || !passwordConfirmation.trim()) {
    errors.passwordConfirmation = t('common.forms.requiredField');
  } else if (password !== passwordConfirmation) {
    errors.passwordConfirmation = t('signUp.validation.pswMatch');
  }

  return errors;
};

export const createValidationFunction = (
  userType: UserTypeEnum | undefined,
  passwordValidator: PasswordValidatorState,
  t: any
) => {
  return (values: SignUpValues): FormikErrors<SignUpValues> => {
    const errors: FormikErrors<SignUpValues> = {};
    const requiredField = t('common.forms.requiredField');

    // Name validation
    errors.firstName = validateName(values.firstName, 'firstName', t);
    errors.lastName = validateName(values.lastName, 'lastName', t);

    // Email validation
    errors.email = validateEmail(values.email, t);

    const requiresPhoneNumber =
      userType === UserTypeEnum.PARTNER ||
      userType === UserTypeEnum.DISTRIBUTOR;

    if (requiresPhoneNumber) {
      // Treat a dial-code-only value as empty so "Required Field" is shown
      // rather than "Phone number is invalid".
      if (
        !values.phoneNumber?.trim() ||
        isDialCodeOnly(values.phoneNumber, values.country)
      ) {
        errors.phoneNumber = requiredField;
      } else if (
        !checkPhoneNumber(values.phoneNumber, values.country || 'US')
      ) {
        errors.phoneNumber = t('signUp.validation.phoneInvalid');
      }
    } else if (values.phoneNumber?.trim()) {
      // Optional field: skip validation when only the country dial code remains
      // (user effectively cleared the field). Validate as soon as any national
      // number digits are present beyond the dial code.
      if (
        !isDialCodeOnly(values.phoneNumber, values.country) &&
        !checkPhoneNumber(values.phoneNumber, values.country || 'US')
      ) {
        errors.phoneNumber = t('signUp.validation.phoneInvalid');
      }
    }

    // Country validation
    if (!values.country || !values.country.trim()) {
      errors.country = requiredField;
    }

    // Password validation
    const passwordErrors = validatePassword(
      values.password,
      values.passwordConfirmation,
      passwordValidator,
      t
    );
    Object.assign(errors, passwordErrors);

    // Terms validation
    if (!values.termsAccepted) {
      errors.termsAccepted = requiredField;
    }

    // Remove undefined errors
    Object.keys(errors).forEach(key => {
      if (errors[key] === undefined) {
        delete errors[key];
      }
    });

    return errors;
  };
};
